import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardContent } from '@/components/ui/Card';
import { ContextMenu } from '@/components/ui/ContextMenu';
import type { ColumnDef } from '@tanstack/react-table';
import { SLBPosition } from '@/types';
import { getColumnId } from '@/types/table.types';
import {
  L2CP_COLUMN_IDS,
  useInventoryColumnStore,
  useInventoryDataStore,
  useInventoryFilterStore,
  useUIStore,
} from '@/stores';
import { useCellSelectionWithCopy } from '@/hooks/useCellSelectionWithCopy';
import {
  applyInventoryLocalFilters,
  applyInventorySearch,
  calculateColumnWidths,
  COLUMN_ID_TO_FILTER_ID,
  formatCellValue,
  getActiveFilterColumnIds,
  getAppliedFilterChips,
  getAppliedFiltersCount,
  getDisplayOptionChips,
} from '@/utils/inventory';
import type { FilterOptionSets } from '@/utils/inventory';
import { computeVisualColIds } from '@/utils/cell-selection-utils';
import { AppliedFiltersStrip } from '../AppliedFiltersStrip';
import { createInventoryColumns } from '../inventory-columns';
import { RefreshStatusLoader } from '../RefreshStatusLoader';
import { ScrollToTopButton } from '../ScrollToTopButton';
import { InventoryTable } from '../tables/InventoryTable';
import {
  DEFAULT_WIDTH as SKELETON_DEFAULT_WIDTH,
  ROW_NUMBER_DEFAULT_WIDTH,
  TableSkeleton,
  getStoredWidths,
  storeColumnWidths,
} from '../tables/TableSkeleton';
import { useInventoryContextMenus } from '../../hooks/useInventoryContextMenus';

const L2CP_CHIP_TO_SECTION: Record<string, string> = {
  l2cpCounterparties: 'l2cpGroup',
  l2cpQuantity: 'l2cpGroup',
  l2cpAmountEur: 'l2cpGroup',
  l2cpRate: 'l2cpGroup',
  l2cpGrossRate: 'l2cpGroup',
  l2cpSnpDelta: 'l2cpGroup',
};

const NUMERIC_COLUMNS = new Set([
  'price',
  'quantity',
  'settledQuantity',
  'quantityT1',
  'quantityT2',
  'quantityTx',
  'cimbEq2Quantity',
  'cimbSfQuantity',
  'cimbSf3Quantity',
  'velocityLQuantity',
  'velocityMQuantity',
  'leverageRate',
  'valueEUR',
  'benchmarkFee',
  'quantityOnLoan',
  'lentToCounterparties',
  'l2cpQuantity',
  'l2cpAmountEur',
  'l2cpRate',
  'l2cpGrossRate',
  'l2cpSnpDelta',
  'activeUtilisation',
  'currencyBenchmark',
  'rebateRate',
  'dailyPotentialEUR',
  'adtv30D',
  'adtvMultiple',
  'adtvAdjustedPotentialEUR',
]);

type InventoryTabPanelProps = {
  allSLBPositions: SLBPosition[];
  isLoading: boolean;
  error: Error | null;
  appliedFiltersExpanded: boolean;
  onAppliedFiltersExpandedChange: (v: boolean) => void;
  onAppliedFiltersCountChange: (n: number) => void;
  onSearchResultCountChange: (n: number) => void;
};

export const InventoryTabPanel: React.FC<InventoryTabPanelProps> = ({
  allSLBPositions,
  isLoading,
  error,
  appliedFiltersExpanded,
  onAppliedFiltersExpandedChange,
  onAppliedFiltersCountChange,
  onSearchResultCountChange,
}) => {
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const sortedDataRef = useRef<SLBPosition[]>([]);
  const inventoryRowCountRef = useRef(0);
  const parentRef = useRef<HTMLDivElement>(null);

  const searchQuery = useInventoryDataStore((state) => state.searchQuery);
  const setSearchQuery = useInventoryDataStore((state) => state.setSearchQuery);
  const sorting = useInventoryDataStore((state) => state.inventorySorting);
  const setSorting = useInventoryDataStore((state) => state.setInventorySorting);
  const setFilteredInventoryData = useInventoryDataStore((state) => state.setFilteredInventoryData);

  const setFilterSidebarCollapsed = useUIStore((state) => state.setFilterSidebarCollapsed);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const setLocateHighlightedColumnIds = useUIStore((state) => state.setLocateHighlightedColumnIds);
  const locateHighlightedColumnIds = useUIStore((state) => state.locateHighlightedColumnIds);
  const isColumnsTabActive = useUIStore((state) => state.columnsTabActive);
  const dateTimePickerOpen = useUIStore((state) => state.dateTimePickerOpen);
  const highlightedColumnIds = locateHighlightedColumnIds ?? [];

  const isForceCollecting = useInventoryDataStore((state) => state.isForceCollecting);
  const isForceCollectSuccess = useInventoryDataStore((state) => state.isForceCollectSuccess);

  const columnConfig = useInventoryColumnStore((state) => state.columns);
  const lentToCounterpartiesGroupExpanded = useInventoryColumnStore(
    (state) => state.lentToCounterpartiesGroupExpanded
  );
  const setLentToCounterpartiesGroupExpanded = useInventoryColumnStore(
    (state) => state.setLentToCounterpartiesGroupExpanded
  );
  const pinnedLeft = useInventoryColumnStore((state) => state.pinnedLeft);
  const pinnedRight = useInventoryColumnStore((state) => state.pinnedRight);
  const pinColumn = useInventoryColumnStore((state) => state.pinColumn);
  const getPinState = useInventoryColumnStore((state) => state.getPinState);
  const toggleColumnVisibility = useInventoryColumnStore((state) => state.toggleColumnVisibility);

  const localInventoryFilters = useInventoryFilterStore((state) => state.localInventoryFilters);
  const setLocalInventoryFilters = useInventoryFilterStore((state) => state.setLocalInventoryFilters);
  const filterDisplayOptions = useInventoryFilterStore((state) => state.filterDisplayOptions);
  const updateFilterDisplayOption = useInventoryFilterStore((state) => state.updateFilterDisplayOption);
  const clearLocalInventoryFilters = useInventoryFilterStore((state) => state.clearLocalInventoryFilters);
  const resetLocalFilter = useInventoryFilterStore((state) => state.resetLocalFilter);
  const filterMetadata = useInventoryFilterStore((state) => state.filterMetadata);

  const allColumns = useMemo(() => createInventoryColumns(setSearchQuery), [setSearchQuery]);

  const columns = useMemo(() => {
    const columnMap = new Map(allColumns.map((col) => [getColumnId(col), col]));
    const rowNumberColumn = columnMap.get('rowNumber');
    const L2CP_GROUP_ID = 'lentToCounterparties';
    const configuredColumns = columnConfig
      .filter((config) => {
        if (!config.visible && config.groupId !== L2CP_GROUP_ID) return false;
        if (config.groupId !== L2CP_GROUP_ID) return config.visible;
        if (config.id === 'lentToCounterparties') return config.visible;
        return lentToCounterpartiesGroupExpanded || config.visible;
      })
      .map((config) => columnMap.get(config.id))
      .filter((col): col is ColumnDef<SLBPosition, unknown> => col !== undefined);
    return rowNumberColumn ? [rowNumberColumn, ...configuredColumns] : configuredColumns;
  }, [columnConfig, allColumns, lentToCounterpartiesGroupExpanded]);

  const allL2cpNestedChecked = useMemo(() => {
    const nestedIds = L2CP_COLUMN_IDS.filter((id) => id !== 'lentToCounterparties');
    return nestedIds.every(
      (id) => columnConfig.find((c: { id: string }) => c.id === id)?.visible === true
    );
  }, [columnConfig]);

  const activeFilterColumnIds = useMemo(
    () => getActiveFilterColumnIds(localInventoryFilters),
    [localInventoryFilters]
  );

  const handleFilterColumnClick = useCallback(
    (columnId: string) => {
      setFilterSidebarCollapsed(false);
      const filterId = COLUMN_ID_TO_FILTER_ID[columnId] ?? columnId;
      setFocusedFilterId(filterId);
      expandFilter(filterId);
      setLocateHighlightedColumnIds(null);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter, setLocateHighlightedColumnIds]
  );

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const inventoryColIds = useMemo(
    () => computeVisualColIds(columns, pinnedLeftSet, pinnedRightSet),
    [columns, pinnedLeftSet, pinnedRightSet]
  );

  const inventoryFormatValue = useCallback(
    (item: SLBPosition, colId: string) => {
      const value = item[colId as keyof SLBPosition];
      const col = columns.find((c) => getColumnId(c) === colId);
      return value == null ? '' : col ? formatCellValue(col, value) : String(value);
    },
    [columns]
  );

  const inventorySelection = useCellSelectionWithCopy<SLBPosition>({
    colIds: inventoryColIds,
    rowCountRef: inventoryRowCountRef,
    scrollContainerRef: parentRef,
    rowHeight: 38,
    headerHeight: 56,
    tableSelector: '[data-table-scroll-container]',
    sortedDataRef,
    formatValue: inventoryFormatValue,
    getHeader: (colId) => columnConfig.find((c) => c.id === colId)?.label ?? colId,
  });

  useEffect(() => {
    inventorySelection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: clear only when sort/filter changes; depending on the whole `inventorySelection` object would refire every time the selection changes (defeating the purpose)
  }, [sorting, localInventoryFilters, inventorySelection.clearSelection]);

  const {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
    cellCtxMenu,
    cellCtxMenuItems,
    handleCellContextMenu,
    closeCellCtxMenu,
    bindScrollClose,
  } = useInventoryContextMenus({
    columnConfig,
    columns,
    sorting,
    setSorting,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    localInventoryFilters,
    setLocalInventoryFilters,
    sortedDataRef,
    colIds: inventoryColIds,
    selectedCellsForCopy: inventorySelection.getSelectedCellsAsSet,
    selectedCellCount: inventorySelection.getSelectedCellCount,
    selectColumn: inventorySelection.selectColumn,
    deselectColumn: inventorySelection.deselectColumn,
    isColumnFullySelected: inventorySelection.isColumnFullySelected,
    colIdToIndex: inventorySelection.colIdToIndex,
  });

  const optionSets = useMemo<FilterOptionSets>(
    () => ({
      accountId: filterMetadata.accountIds,
      counterparty: filterMetadata.counterparties,
      currency: filterMetadata.currencies,
      exchange: filterMetadata.exchanges,
      caStatus: filterMetadata.caStatuses,
      brokerAccountName: filterMetadata.brokerAccountNames,
      brokerSettlementCounterparty: filterMetadata.brokerSettlementCounterparties,
      l2cpCounterparties: filterMetadata.l2cpCounterparties?.length
        ? filterMetadata.l2cpCounterparties
        : filterMetadata.counterparties,
    }),
    [filterMetadata]
  );

  const appliedFilterChips = useMemo(
    () => [
      ...getAppliedFilterChips(localInventoryFilters, optionSets),
      ...getDisplayOptionChips(filterDisplayOptions),
    ],
    [localInventoryFilters, filterDisplayOptions, optionSets]
  );

  const appliedFiltersCount = useMemo(
    () => getAppliedFiltersCount(localInventoryFilters, filterDisplayOptions, optionSets),
    [localInventoryFilters, filterDisplayOptions, optionSets]
  );

  const handleAppliedChipClick = useCallback(
    (filterId: string) => {
      setFilterSidebarCollapsed(false);
      const baseId = filterId.includes(':') ? filterId.split(':')[1] : filterId;
      const sectionId = L2CP_CHIP_TO_SECTION[baseId] ?? baseId;
      setFocusedFilterId(sectionId);
      expandFilter(sectionId);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter]
  );

  const handleAppliedChipRemove = useCallback(
    (filterId: string) => {
      if (filterId.startsWith('hideZero:')) {
        updateFilterDisplayOption(filterId.slice('hideZero:'.length), 'hideZero', false);
      } else if (filterId.startsWith('hideBlanks:')) {
        updateFilterDisplayOption(filterId.slice('hideBlanks:'.length), 'hideBlanks', false);
      } else {
        resetLocalFilter(filterId);
      }
    },
    [updateFilterDisplayOption, resetLocalFilter]
  );

  const filteredData = useMemo(() => {
    const locallyFiltered = applyInventoryLocalFilters(allSLBPositions, localInventoryFilters, filterDisplayOptions);
    return applyInventorySearch(locallyFiltered, searchQuery, allColumns);
  }, [allSLBPositions, localInventoryFilters, filterDisplayOptions, searchQuery, allColumns]);

  const getLentToCounterpartiesSortValue = useCallback((row: SLBPosition): number => {
    if (row.l2cpQuantity != null && typeof row.l2cpQuantity === 'number') {
      return row.l2cpQuantity;
    }
    return row.loans?.reduce((sum, loan) => sum + loan.quantityLent, 0) ?? 0;
  }, []);

  const sortedData = useMemo(() => {
    if (sorting.length === 0) {
      return filteredData;
    }

    const sorted = [...filteredData].sort((a, b) => {
      for (const sort of sorting) {
        let aValue: unknown;
        let bValue: unknown;
        if (sort.id === 'lentToCounterparties') {
          aValue = getLentToCounterpartiesSortValue(a);
          bValue = getLentToCounterpartiesSortValue(b);
        } else {
          aValue = a[sort.id as keyof SLBPosition];
          bValue = b[sort.id as keyof SLBPosition];
        }

        if (aValue === undefined || aValue === null) {
          if (bValue === undefined || bValue === null) continue;
          return 1;
        }
        if (bValue === undefined || bValue === null) {
          return -1;
        }

        if (NUMERIC_COLUMNS.has(sort.id)) {
          const aNum = Number(aValue);
          const bNum = Number(bValue);
          if (aNum < bNum) return sort.desc ? 1 : -1;
          if (aNum > bNum) return sort.desc ? -1 : 1;
          continue;
        }

        if ((aValue as string | number) < (bValue as string | number)) return sort.desc ? 1 : -1;
        if ((aValue as string | number) > (bValue as string | number)) return sort.desc ? -1 : 1;
      }
      return 0;
    });

    return sorted;
  }, [filteredData, sorting, getLentToCounterpartiesSortValue]);

  sortedDataRef.current = sortedData;
  inventoryRowCountRef.current = sortedData.length;

  useEffect(() => {
    setFilteredInventoryData(sortedData);
  }, [sortedData, setFilteredInventoryData]);

  useEffect(() => {
    onAppliedFiltersCountChange(appliedFiltersCount);
  }, [appliedFiltersCount, onAppliedFiltersCountChange]);

  useEffect(() => {
    onSearchResultCountChange(sortedData.length);
  }, [sortedData.length, onSearchResultCountChange]);

  const columnWidths = useMemo(() => {
    if (allSLBPositions.length === 0 || allColumns.length === 0) return getStoredWidths();
    const calculated = calculateColumnWidths(allColumns, allSLBPositions);
    storeColumnWidths(calculated);
    const stored = getStoredWidths();
    const merged: Record<string, number> = {};
    const defaultForColumn = (id: string): number =>
      id === 'rowNumber' ? ROW_NUMBER_DEFAULT_WIDTH : SKELETON_DEFAULT_WIDTH;
    for (const [id, w] of Object.entries(calculated)) {
      merged[id] = Math.max(w, stored[id] ?? defaultForColumn(id));
    }
    return merged;
  }, [allColumns, allSLBPositions]);

  useEffect(() => {
    return bindScrollClose(parentRef.current);
  }, [bindScrollClose]);

  const scrollToTop = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  return (
    <>
      <AppliedFiltersStrip
        count={appliedFiltersCount}
        chips={appliedFilterChips}
        onChipClick={handleAppliedChipClick}
        onChipRemove={handleAppliedChipRemove}
        onClearAll={clearLocalInventoryFilters}
        hideTrigger
        expanded={appliedFiltersExpanded}
        onToggleExpanded={onAppliedFiltersExpandedChange}
      />
      <CardContent className="flex flex-1 flex-col overflow-hidden p-0 border-t border-gray-200 relative">
        {isLoading && sortedData.length === 0 ? (
          <TableSkeleton columns={columns} pinnedLeft={pinnedLeft} pinnedRight={pinnedRight} />
        ) : (
          <InventoryTable
            data={sortedData}
            columns={columns}
            sorting={sorting}
            onSortChange={setSorting}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            columnWidths={columnWidths}
            isLoading={isLoading}
            error={error}
            hoveredColumn={hoveredColumn}
            onHoveredColumnChange={setHoveredColumn}
            onShowScrollTop={setShowScrollTop}
            scrollContainerRef={parentRef}
            l2cpGroupExpanded={lentToCounterpartiesGroupExpanded}
            onToggleL2cpGroup={() =>
              setLentToCounterpartiesGroupExpanded(!lentToCounterpartiesGroupExpanded)
            }
            allL2cpNestedChecked={allL2cpNestedChecked}
            activeFilterColumnIds={activeFilterColumnIds}
            onFilterColumnClick={handleFilterColumnClick}
            highlightedColumnIds={highlightedColumnIds}
            pinnedLeft={pinnedLeft}
            pinnedRight={pinnedRight}
            onPinColumn={(colId, side) => pinColumn(colId, side)}
            onContextMenuColumn={handleColumnContextMenu}
            onContextMenuCell={handleCellContextMenu}
            isCellSelected={inventorySelection.isCellSelected}
            isCellActive={inventorySelection.isCellActive}
            isRowSelected={inventorySelection.isRowSelected}
            isColSelected={inventorySelection.isColSelected}
            getCellBorders={inventorySelection.getCellBorders}
            colIdToIndex={inventorySelection.colIdToIndex}
            selectionTrigger={inventorySelection.selection}
            ensureCellSelectedForContextMenu={inventorySelection.ensureCellSelectedForContextMenu}
            onCellMouseDown={inventorySelection.handleCellMouseDown}
            onCellMouseEnter={inventorySelection.handleCellMouseEnter}
            onCellMouseUp={inventorySelection.handleCellMouseUp}
            isColumnsTabActive={isColumnsTabActive}
          />
        )}
        <RefreshStatusLoader
          isVisible={(isForceCollecting || isForceCollectSuccess) && !dateTimePickerOpen}
          isSuccess={isForceCollectSuccess}
          isBackToTopVisible={showScrollTop}
        />
      </CardContent>
      <ScrollToTopButton show={showScrollTop} onClick={scrollToTop} />
      {headerCtxMenu && (
        <ContextMenu
          items={headerCtxMenuItems}
          position={{ x: headerCtxMenu.x, y: headerCtxMenu.y }}
          onClose={closeHeaderCtxMenu}
        />
      )}
      {cellCtxMenu && (
        <ContextMenu
          items={cellCtxMenuItems}
          position={{ x: cellCtxMenu.x, y: cellCtxMenu.y }}
          onClose={closeCellCtxMenu}
        />
      )}
    </>
  );
};
